/**
 * Country → State/Region → Location, as controls.
 *
 * One set of controls over one selection (`useWorkingLocation`). The sidebar
 * shows them; a page that used to carry its own country/state/location filter
 * shows the very same controls inline instead, so changing either one changes
 * both and there is never a second, disagreeing geographic choice on screen.
 *
 * Native selects on purpose: they are compact in a sidebar, and on a phone
 * they open the platform's own picker rather than a popover that fights the
 * drawer for the screen.
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown, Lock, MapPin } from 'lucide-react';
import { useWorkingLocation } from '../lib/locationContext';
import { describeSelection } from '../lib/geoScope';
import { cn } from './ui';

/** The active scope as a breadcrumb: "Nigeria › Enugu › All locations". */
export function useScopeLabel(): { parts: string[]; everywhere: boolean; text: string } {
  const { selection, location } = useWorkingLocation();
  const d = describeSelection(selection, location?.name ?? null);
  return { ...d, text: d.parts.join(' › ') };
}

function ScopeSelect({
  id,
  label,
  value,
  onChange,
  options,
  disabled,
  compact,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  compact: boolean;
}): ReactNode {
  return (
    <div className={compact ? 'space-y-0.5' : 'min-w-0 space-y-1'}>
      <label
        htmlFor={id}
        className={cn(
          'block font-medium',
          compact ? 'text-[11px] uppercase tracking-wide text-slate-400' : 'text-sm text-slate-700',
        )}
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'input-base pr-8 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          compact && 'h-8 py-1 text-sm',
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * The three cascading controls.
 *
 * Changing the country resets the region and location; changing the region
 * resets a location outside it — the rules live in `lib/geoScope.ts`, so the
 * sidebar and a page cannot apply them differently.
 */
export function GeoScopeFields({
  compact = false,
  bare = false,
  idPrefix = 'scope',
}: {
  compact?: boolean;
  /** Three loose fields, for a page's own filter grid, instead of a group. */
  bare?: boolean;
  idPrefix?: string;
}): ReactNode {
  const {
    selection,
    countries,
    regions,
    regionCountry,
    scopedLocations,
    regionTerm,
    canAggregate,
    setCountry,
    setRegion,
    setBranchId,
  } = useWorkingLocation();

  const regionLabel = regionCountry ? regionTerm.singular : 'State / Region';
  const noRegions = Boolean(regionCountry) && regions.length === 0;

  const fields = (
    <>
      <ScopeSelect
        id={`${idPrefix}-country`}
        label="Country"
        compact={compact}
        value={selection.country ?? ''}
        onChange={(v) => setCountry(v || null)}
        disabled={!canAggregate}
        options={[...(canAggregate ? [{ value: '', label: 'All countries' }] : []), ...countries]}
      />
      <ScopeSelect
        id={`${idPrefix}-region`}
        label={regionLabel}
        compact={compact}
        value={selection.region ?? ''}
        onChange={(v) => setRegion(v || null)}
        disabled={!canAggregate || !regionCountry || noRegions}
        options={
          noRegions
            ? [{ value: '', label: `No ${regionTerm.plural.toLowerCase()} listed` }]
            : !regionCountry
              ? [{ value: '', label: 'All states / regions' }]
              : [...(canAggregate ? [{ value: '', label: `All ${regionTerm.plural.toLowerCase()}` }] : []), ...regions]
        }
      />
      <ScopeSelect
        id={`${idPrefix}-location`}
        label="Location"
        compact={compact}
        value={selection.branchId ?? ''}
        onChange={(v) => setBranchId(v || null)}
        disabled={!canAggregate && scopedLocations.length <= 1}
        options={[
          ...(canAggregate ? [{ value: '', label: 'All locations' }] : []),
          ...scopedLocations.map((l) => ({ value: l.id, label: l.name })),
        ]}
      />
    </>
  );
  if (bare) return fields;
  return (
    <div className={compact ? 'space-y-2' : 'flex flex-wrap gap-3'} role="group" aria-label="Geographic scope">
      {fields}
    </div>
  );
}

/**
 * The sidebar's scope: the active breadcrumb, opening onto the three controls.
 *
 * A login confined to one location sees where it is, locked, and nothing to
 * change — a control that cannot change anything is noise. Everyone else sees
 * the breadcrumb and opens the controls with it.
 */
export function SidebarScope(): ReactNode {
  const { isLoading, canAggregate, locations } = useWorkingLocation();
  const label = useScopeLabel();
  const [open, setOpen] = useState(false);

  if (isLoading || locations.length === 0) return null;

  if (!canAggregate && locations.length <= 1) {
    return (
      <p className="mt-1.5 flex items-center gap-2 px-2.5 py-1.5 text-sm text-slate-600" data-testid="sidebar-scope">
        <Lock className="size-3.5 shrink-0 text-slate-400" aria-hidden />
        <span className="min-w-0 flex-1 truncate" title={label.text}>
          {label.text}
        </span>
      </p>
    );
  }

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="sidebar-scope-fields"
        aria-label={`Geographic scope: ${label.text}`}
        className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-slate-50"
        data-testid="sidebar-scope"
      >
        <MapPin className={cn('size-3.5 shrink-0', label.everywhere ? 'text-slate-400' : 'text-brand-600')} aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm text-slate-700" title={label.text}>
          {label.parts.map((p, i) => (
            <span key={i}>
              {i > 0 ? <span className="text-slate-300"> › </span> : null}
              <span className={i === label.parts.length - 1 ? 'font-medium text-slate-900' : undefined}>{p}</span>
            </span>
          ))}
        </span>
        <ChevronDown className={cn('size-3.5 shrink-0 text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div id="sidebar-scope-fields" className="mt-1 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
          <GeoScopeFields compact idPrefix="sidebar-scope" />
        </div>
      ) : null}
    </div>
  );
}
